import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EramCpdlcDialog, abnormalCpdlcDialogStates, deleteCpdlcDialogStates } from "@poscon/eram-cpdlc";
import { useViewOptions } from "../contexts/viewOptionContext.js";
import { backgroundOption, borderOption, brightOption, counterOption, fontOption } from "../viewOptionFactory.js";
import { eramTextDimensionMap } from "../../eramFont.js";
import { MenuSegment } from "./MenuSegment.js";
import { colorNameMap } from "../../colorNameMap.js";
import { cpdlcSessionsSelector, flightplansSelector, useRootSelector } from "../stateSelectors.js";
import { CpdlcMsgTable, getCpdlcMsgRowHeight } from "./CpdlcMsgTable.js";
import { sectorIdSelector } from "../slices/connectionSlice.js";
import { baseEramHubConnection } from "../../eramHubConnectionFactory.js";
export const initialMsgOutOptions = {
    background: true,
    border: true,
    naLines: 6,
    saLines: 9,
    text: 20,
    bright: 100,
    font: 2,
};
export const cpdlcMsgOutView = "CPDLC_MSGOUT";
const optionMap = {
    background: backgroundOption(cpdlcMsgOutView),
    border: borderOption(cpdlcMsgOutView),
    saLines: counterOption(cpdlcMsgOutView, "saLines", "SA LINES", 2, 20, 11),
    naLines: counterOption(cpdlcMsgOutView, "naLines", "NA LINES", 2, 10, 11),
    font: fontOption(cpdlcMsgOutView),
    bright: brightOption(cpdlcMsgOutView),
    text: counterOption(cpdlcMsgOutView, "text", "TEXT", 10, 30, 7),
};
export const CpdlcMsgOut = ({ ViewComponent, openTocMenu }) => {
    const viewOptions = useViewOptions();
    const cpdlcSessions = useRootSelector(cpdlcSessionsSelector);
    const flightplans = useRootSelector(flightplansSelector);
    const sectorId = useRootSelector(sectorIdSelector);
    const fpDialogs = Object.entries(cpdlcSessions).reduce((acc, [fpId, session]) => {
        const fp = flightplans[fpId];
        if (fp) {
            for (const dialog of Object.values(session.dialogMap)) {
                if (dialog.eligiblePosition?.sectorId === sectorId &&
                    !dialog.deleted &&
                    dialog.messages[0].type === "UM") {
                    const list = acc[abnormalCpdlcDialogStates.includes(dialog.eramState) ? "special" : "normal"];
                    const item = { fp, dialog };
                    list.push(item);
                }
            }
            if (sectorId && session.eligibleSectorId === sectorId && session.heldTOCs.length > 0) {
                acc.normal.push({ fp, tocMsgs: session.heldTOCs });
            }
        }
        return acc;
    }, { normal: [], special: [] });
    const { special: specialList, normal: normalList } = fpDialogs;
    const { font, bright } = viewOptions;
    const fontDim = eramTextDimensionMap[font];
    const alpha = bright / 100;
    const cleanupDialogs = [...specialList, ...normalList].reduce((acc, d) => {
        const dialog = d.dialog;
        if (dialog &&
            ((abnormalCpdlcDialogStates.includes(dialog.eramState) && dialog.acknowledgedAt) ||
                (!abnormalCpdlcDialogStates.includes(dialog.eramState) &&
                    (deleteCpdlcDialogStates.includes(dialog.eramState) || EramCpdlcDialog.dialogIsClosed(dialog))))) {
            acc.push({ fpId: d.fp.id, dialogId: dialog.id });
        }
        return acc;
    }, []);
    const headerButtons = [
        {
            text: "CLEANUP",
            disabled: cleanupDialogs.length === 0,
            onmousedown: () => {
                baseEramHubConnection.deleteCpdlcDialogs(cleanupDialogs);
            },
        },
    ];
    const width = fontDim.width * 51;
    const rowHeight = getCpdlcMsgRowHeight(font);
    const specialHeightNum = Math.min(specialList.length + 2, viewOptions.saLines + 2);
    const specialListHeight = specialList.length > 0 ? rowHeight * specialHeightNum + 3 * Math.floor(specialHeightNum / 3) : 0;
    const normalHeightNum = Math.min(normalList.length + 2, viewOptions.naLines + 2);
    const normalListHeight = rowHeight * normalHeightNum + 3 * Math.floor(normalHeightNum / 3);
    const height = specialListHeight + normalListHeight - 1;
    return (_jsxs(ViewComponent, { view: cpdlcMsgOutView, headerButtons: headerButtons, optionMap: optionMap, width: 51, height: height, children: [specialList.length > 0 && (_jsxs("container", { zIndex: 2, sortableChildren: true, children: [_jsx(MenuSegment, { fillColor: colorNameMap.mediumGrey, text: "SPECIAL AREA", width: width, fontSize: font }), _jsx(CpdlcMsgTable, { path: "SPECIAL_AREA", y: fontDim.height + 3, fpDialogs: specialList, totalWidth: width, fontSize: font, alpha: alpha, view: cpdlcMsgOutView, maxRows: viewOptions.saLines, noSort: true, openTocMenu: openTocMenu })] })), _jsxs("container", { y: specialListHeight, zIndex: 1, sortableChildren: true, children: [_jsx(MenuSegment, { fillColor: colorNameMap.mediumGrey, text: "NORMAL AREA", width: width, fontSize: font }), _jsx(CpdlcMsgTable, { path: "NORMAL_AREA", y: fontDim.height + 3, fpDialogs: normalList, totalWidth: width, fontSize: font, alpha: alpha, view: cpdlcMsgOutView, openTocMenu: openTocMenu, maxRows: viewOptions.naLines })] })] }));
};
//# sourceMappingURL=CpdlcMsgOut.js.map